import { Collection } from '@/kernel/collection';

import StringCell from '@/components/cells/string-cell.vue';
import DateCell from '@/components/cells/date-cell.vue';
import BooleanCell from '@/components/cells/boolean-cell.vue';
import EditCollectionCell from '@/components/cells/edit-collection-cell.vue';
import CollectionNameCell from '@/components/cells/collection-name-cell.vue';
import AssessmentActionsCell from '@/components/cells/assessment-actions-cell.vue';

import { DateRangeFilterAdapter, SelectFilterAdapter, SingleRelationFilterAdapter, YearRangeFilterAdapter, SingleRelationInputAdapter, MultipleRelationInputAdapter, OrganisationFilterContextAdapter, OrganisationFormContextAdapter, ObjectivePickerInputAdapter } from '@/kernel/adapters';

import DateRangeInput from '@/components/inputs/date-range-input.vue';
import TextInput from '@/components/inputs/text-input.vue';
import CheckboxInput from '@/components/inputs/checkbox-input.vue';
import SelectInput from '@/components/inputs/select-input.vue';
import SingleRelationInput from '@/components/inputs/single-relation-input.vue';
import MultipleRelationInput from '@/components/inputs/multiple-relation-input.vue';
import YearRangeInput from '@/components/inputs/year-range-input.vue';
import ObjectivePickerInput from '@/components/inputs/objective-picker-input.vue';
import NumberInput from '@/components/inputs/number-input.vue';

const statusFilterOptions = [
  {label: 'Any', value: null},
  {label: 'Draft', value: 'draft'},
  {label: 'Complete', value: 'complete'}
];

const halfYearOptions = [
  {label: 'Select...', value: null},
  {label: '1st', value: 1},
  {label: '2nd', value: 2}
];

export class Assessment extends Collection {
  config = {
    autoRoute: true,
    requiresOrganisation: true,
    key: 'assessment',
    pluralLabel: 'Assessments',
    singleLabel: 'Assessment',
    indexUrl: '/assessment',
    createUrl: '/assessment/create',
    searchKeys: ['organisationAsset.name', 'organisationAssetManager.name', 'year', 'assessmentTemplate.name'],
    editUrlPrefix: '/assessment',
    idKey: 'id',
    nameKey: 'name',
    payloadEndpoint: '/assessment',
    defaultSort: {
      key: 'name',
      order: 1
    },
    indexColumns: [
      {name: 'Actions', key: 'edit', sortable: false, cell: AssessmentActionsCell, props: {collection: 'assessment'}},
      {name: 'Template', key: 'assessmentTemplate', sortable: true, cell: CollectionNameCell, props: {collection: 'assessment-template'}},
      {name: 'Asset', key: 'organisationAsset', sortable: true, cell: CollectionNameCell, props: {collection: 'organisation-asset'}},
      {name: 'Asset Manager', key: 'organisationAssetManager', sortable: true, cell: CollectionNameCell, props: {collection: 'organisation-asset-manager'}},
      {name: 'Year', key: 'year', sortable: true, cell: StringCell},
      {name: 'Half Year', key: 'halfYear', sortable: false, cell: StringCell},
      {name: 'Status', key: 'status', sortable: true, cell: StringCell},
      {name: 'Updated', key: 'updatedAt', sortable: true, cell: DateCell},
      {name: 'Created', key: 'createdAt', sortable: true, cell: DateCell}
    ],
    indexFilters: [
      {key: 'assessmentTemplate', label: 'Template', input: SingleRelationInput, adapter: SingleRelationFilterAdapter('assessment-template'), props: {label: 'Template', collection: 'assessment-template'}},
      {key: 'organisationAsset', label: 'Asset', input: SingleRelationInput, adapter: SingleRelationFilterAdapter('organisation-asset'), props: {label: 'Asset', collection: 'organisation-asset'}},
      {key: 'organisationAssetManager', label: 'Asset', input: SingleRelationInput, adapter: SingleRelationFilterAdapter('organisation-asset-manager'), props: {label: 'Asset Manager', collection: 'organisation-asset-manager'}},
      {key: 'year', label: 'Year', input: YearRangeInput, adapter: YearRangeFilterAdapter, props: {label: 'Year'}},
      {key: 'status', label: 'Status', input: SelectInput, adapter: SelectFilterAdapter, props: {label: 'Status', options: statusFilterOptions}},
      {key: 'updatedAt', label: 'Updated at', input: DateRangeInput, adapter: DateRangeFilterAdapter, props: {label: 'Updated at'}},
      {key: 'createdAt', label: 'Created at', input: DateRangeInput, adapter: DateRangeFilterAdapter, props: {label: 'Created at'}}
    ],
    indexContexts: [
      {key: 'organisation', adapter: OrganisationFilterContextAdapter}
    ],
    createFormContexts: [
      {key: 'organisation', adapter: OrganisationFormContextAdapter}
    ],
    createFormFields: [
      {key: 'assessmentTemplate', label: 'Template', input: SingleRelationInput, adapter: SingleRelationInputAdapter('assessment-template'), props: {label: 'Assessment Template', collection: 'assessment-template'}},
      {key: 'organisationAsset', label: 'Asset', input: SingleRelationInput, adapter: SingleRelationInputAdapter('organisation-asset'), props: {label: 'Asset', collection: 'organisation-asset'}},
      {key: 'organisationAssetManager', label: 'Asset Manager (optional)', input: SingleRelationInput, adapter: SingleRelationInputAdapter('organisation-asset-manager'), props: {label: 'Asset', collection: 'organisation-asset-manager'}},
      {key: 'year', label: 'Year', input: NumberInput, props: {label: 'Year'}},
      {key: 'halfYear', label: 'Half Year (optional)', input: SelectInput, props: {label: 'Half Year', options: halfYearOptions}},
    ],
    editFormFields: [
      {key: 'assessmentTemplate', label: 'Template', input: SingleRelationInput, adapter: SingleRelationInputAdapter('assessment-template'), props: {label: 'Assessment Template', collection: 'assessment-template'}},
      {key: 'organisationAsset', label: 'Asset', input: SingleRelationInput, adapter: SingleRelationInputAdapter('organisation-asset'), props: {label: 'Asset', collection: 'organisation-asset'}},
      {key: 'organisationAssetManager', label: 'Asset Manager (optional)', input: SingleRelationInput, adapter: SingleRelationInputAdapter('organisation-asset-manager'), props: {label: 'Asset', collection: 'organisation-asset-manager'}},
      {key: 'year', label: 'Year', input: NumberInput, props: {label: 'Year'}},
      {key: 'halfYear', label: 'Half Year (optional)', input: SelectInput, props: {label: 'Half Year', options: halfYearOptions}},
    ]
  }
  
  getInterviewUrl(item) {
    return `/assessment/${item.id}/interview`;
  }
  
  getApiLockEndpoint(id) {
    return `/assessment/${id}/lock`;
  }
  
  getApiUnlockEndpoint(id) {
    return `/assessment/${id}/unlock`;
  }
  
  getApiInterviewIndexEndpoint() {
    return `/assessment-interview`;
  }
  
  getApiInterviewObjectEndpoint(id) {
    return `/assessment-interview/${id}`;
  }
  
  getRedirectAfterCreate(item) {
    return this.getInterviewUrl(item);
  }
  
  getRedirectAfterEdit(item) {
    return this.getInterviewUrl(item);
  }
  
}